import React from 'react';
import { formatCurrency, formatCurrencyShort } from '@vanilla/utils/Utils.js';
import { PAYMENT_MODELS, isRecurringPaymentModel } from '@vanilla/constants/PaymentModels.js';
import { PAYMENT_TERMS } from '@vanilla/constants/PaymentTerms.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { DealFormSelect } from './DealFormSelect.jsx';
import DealRevenueSchedulePanel from './DealRevenueSchedulePanel.jsx';
import { FieldHelpIcon } from '@/components/ui/FieldHelpIcon.jsx';

export default function DealFormRevenuePanel({
    form,
    errors,
    set,
    setField,
    contractValue,
    isClosedWon,
    panelForModel,
    taxAmount,
    totalWithTax,
    milestoneScheduled,
    milestoneRemaining,
    customScheduled,
    customRemaining,
    handlePaymentModelChange,
    handlePaymentCountChange,
    handleDivideEquallyChange,
    addMilestone,
    updateMilestone,
    removeMilestone,
    addCustomRow,
    updateCustomRow,
    removeCustomRow,
}) {
    return (
        <div className="premium-card" id="sectionRevenueCard" data-stepper-panel="revenue">
            <div className="premium-card-header">
                <h3 className="premium-card-title"><span className="premium-card-number">2</span> Revenue & Billing</h3>
                <p className="premium-card-subtitle">Configure billing, payment terms, and invoice schedule for this deal.</p>
            </div>

            <fieldset className="form-section">
                <legend>Contract Information</legend>
                <div className="deal-form-grid-1">
                    <div className="deal-form-inline-pair">
                        <div className="form-group" data-field="dealContractValue">
                            <label htmlFor="dealContractValue">Contract Value <span className="required-asterisk">*</span> <FieldHelpIcon fieldId="dealContractValue" /></label>
                            <div className="input-adorned">
                                <span className="input-prefix">$</span>
                                <Input unstyled type="text" id="dealContractValue" className="form-input" readOnly value={contractValue ? String(contractValue) : ''} />
                            </div>
                        </div>
                        <div className="form-group" data-field="dealPaymentTerms">
                            <label htmlFor="dealPaymentTerms">Payment Terms {isClosedWon && <span className="required-asterisk">*</span>} <FieldHelpIcon fieldId="dealPaymentTerms" /></label>
                            <DealFormSelect
                                id="dealPaymentTerms"
                                value={form.dealPaymentTerms}
                                allowEmpty={false}
                                onValueChange={(v) => setField('dealPaymentTerms', v)}
                                options={PAYMENT_TERMS.map((t) => ({ value: t.value, label: t.label }))}
                            />
                            {errors.dealPaymentTerms && <span className="field-error">{errors.dealPaymentTerms}</span>}
                        </div>
                    </div>
                    <div className="deal-form-inline-pair">
                        <div className="form-group" data-field="dealPaymentModel">
                            <label htmlFor="dealPaymentModel">Payment Model {isClosedWon && <span className="required-asterisk">*</span>} <FieldHelpIcon fieldId="dealPaymentModel" /></label>
                            <DealFormSelect
                                id="dealPaymentModel"
                                value={form.dealPaymentModel}
                                allowEmpty={false}
                                onValueChange={handlePaymentModelChange}
                                options={PAYMENT_MODELS.map((m) => ({ value: m.value, label: m.label }))}
                            />
                            {errors.dealPaymentModel && <span className="field-error">{errors.dealPaymentModel}</span>}
                        </div>
                        {isRecurringPaymentModel(form.dealPaymentModel) && (
                                <div className="form-group" data-field="dealPaymentCount" id="dealPaymentCountGroup">
                                <label htmlFor="dealPaymentCount">Number of Payments {isClosedWon && <span className="required-asterisk">*</span>} <FieldHelpIcon fieldId="dealPaymentCount" /></label>
                                <div className="input-inline-hint">
                                    <Input
                                        unstyled
                                        type="text"
                                        id="dealPaymentCount"
                                        inputMode="numeric"
                                        className="form-input"
                                        value={form.dealPaymentCount}
                                        placeholder="Enter number of payments"
                                        onChange={(e) => handlePaymentCountChange(e.target.value.replace(/\D/g, ''))}
                                    />
                                    <span className="field-hint" id="dealPaymentCountUnit">payments</span>
                                </div>
                                {errors.dealPaymentCount && <span className="field-error">{errors.dealPaymentCount}</span>}
                            </div>
                        )}
                    </div>
                </div>
            </fieldset>

            <fieldset className="form-section" id="billingScheduleSection">
                <legend>Billing Schedule</legend>

                <div id="dealBillingTaxControls" className="billing-tax-controls">
                    <div className="billing-tax-toolbar">
                        <div className="toggle-label-group">
                            <label htmlFor="dealTaxEnabled">Tax Enabled <FieldHelpIcon fieldId="dealInvoiceAmount" /></label>
                            <Switch
                                id="dealTaxEnabled"
                                checked={!!form.taxIncluded}
                                onCheckedChange={(v) => setField('taxIncluded', v)}
                            />
                        </div>
                    </div>
                    {form.taxIncluded && (
                        <div className="billing-tax-calc" id="dealTaxCalcRow">
                            <div className="billing-tax-fields">
                                <div className="form-group">
                                    <label htmlFor="dealTaxRate" className="flex items-center justify-between gap-2">
                                        <span>{form.taxUsePercent === false ? 'Tax Amount' : 'Tax Rate'}</span>
                                        <span className="flex items-center gap-1 text-xs">
                                            <span className={form.taxUsePercent === false ? 'font-semibold' : 'opacity-60'}>$</span>
                                            <Switch id="dealTaxUsePercent" checked={form.taxUsePercent !== false} onCheckedChange={(v) => setField('taxUsePercent', v)} aria-label="Tax as percentage" />
                                            <span className={form.taxUsePercent !== false ? 'font-semibold' : 'opacity-60'}>%</span>
                                        </span>
                                    </label>
                                    <div className="input-adorned">
                                        {form.taxUsePercent === false && <span className="input-prefix">$</span>}
                                        <Input unstyled type="text" inputMode="decimal" id="dealTaxRate" className="form-input" value={form.taxRate ?? ''} placeholder={form.taxUsePercent === false ? 'Enter tax amount' : 'Enter tax rate'} onChange={(e) => setField('taxRate', e.target.value.replace(/[^0-9.]/g, ''))} />
                                        {form.taxUsePercent !== false && <span className="input-suffix">%</span>}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Tax Amount</label>
                                    <strong id="dealTaxAmount" className="billing-tax-value">{formatCurrency(taxAmount)}</strong>
                                </div>
                            </div>
                            <div className="billing-tax-total">
                                <label>Total Amount</label>
                                <strong id="dealTotalWithTax">{formatCurrency(totalWithTax)}</strong>
                            </div>
                        </div>
                    )}
                </div>

                {panelForModel === 'recurring' && (
                    <div id="dealRecurringBillingPanel" className="billing-schedule-panel">
                        <div className="billing-section-header">
                            <label className="billing-section-label">Payment Schedule</label>
                            <div className="toggle-label-group">
                                <label htmlFor="dealDivideEqually">Divide amount equally</label>
                                <Switch
                                    id="dealDivideEqually"
                                    checked={!!form.divideEqually}
                                    onCheckedChange={handleDivideEquallyChange}
                                />
                            </div>
                        </div>
                        <div className="billing-table-wrapper">
                            <table className="billing-table" id="dealRecurringScheduleTable">
                                <thead>
                                    <tr>
                                        <th className="col-sno">#</th>
                                        <th className="col-date">Date</th>
                                        <th className="col-amount">Amount</th>
                                    </tr>
                                </thead>
                                <tbody id="dealRecurringScheduleRows">
                                    {(form.recurringScheduleRows || []).map((row, i) => (
                                        <tr key={i}>
                                            <td className="col-sno">{i + 1}</td>
                                            <td className="col-date">
                                                <Input unstyled type="date" className="form-input" value={row.date || ''} onChange={(e) => { const rows = [...(form.recurringScheduleRows || [])]; rows[i] = { ...rows[i], date: e.target.value }; set({ recurringScheduleRows: rows }); }} />
                                            </td>
                                            <td className="col-amount">
                                                <Input unstyled type="text" inputMode="decimal" className="form-input" value={row.amount ?? ''} onChange={(e) => {
                                                    const rows = [...(form.recurringScheduleRows || [])];
                                                    rows[i] = { ...rows[i], amount: e.target.value.replace(/[^0-9.]/g, '') };
                                                    set({ recurringScheduleRows: rows });
                                                }} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {panelForModel === 'oneTime' && (
                    <div id="dealOneTimePanel" className="billing-schedule-panel">
                        <div className="billing-schedule-panel-section">
                            <label className="billing-section-label">Payment Details</label>
                            <div className="deal-form-grid-1">
                                <div className="deal-form-inline-pair">
                                    <div className="form-group" data-field="dealOneTimeDate">
                                        <label htmlFor="dealOneTimeDate">Payment Date</label>
                                        <Input unstyled type="date" className="form-input" value={form.oneTimeDate} onChange={(e) => setField('oneTimeDate', e.target.value)} />
                                    </div>
                                    <div className="form-group" data-field="dealOneTimeAmount">
                                        <label htmlFor="dealOneTimeAmount">Amount</label>
                                        <div className="input-adorned"><span className="input-prefix">$</span><Input unstyled type="text" inputMode="decimal" id="dealOneTimeAmount" step="0.01" className="form-input" placeholder="Enter amount" value={form.oneTimeAmount} onChange={(e) => setField('oneTimeAmount', e.target.value)} /></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {panelForModel === 'ongoing' && (
                    <div id="dealOngoingPanel" className="billing-schedule-panel">
                        <div className="deal-form-grid-1">
                            <div className="deal-form-inline-pair">
                                <div className="form-group" data-field="dealRetainerAmount">
                                    <label htmlFor="dealRetainerAmount" className="flex items-center justify-between gap-2">
                                        <span>Monthly Retainer</span>
                                        <span className="flex items-center gap-1 text-xs">
                                            <span className={!form.retainerUsePercent ? 'font-semibold' : 'opacity-60'}>$</span>
                                            <Switch id="dealRetainerUsePercent" checked={!!form.retainerUsePercent} onCheckedChange={(v) => setField('retainerUsePercent', v)} aria-label="Retainer as percentage" />
                                            <span className={form.retainerUsePercent ? 'font-semibold' : 'opacity-60'}>%</span>
                                        </span>
                                    </label>
                                    <div className="input-adorned" id="dealRetainerInputWrap">
                                        {!form.retainerUsePercent && <span className="input-prefix" id="dealRetainerPrefix">$</span>}
                                        <Input unstyled type="text" inputMode="decimal" id="dealRetainerAmount" className="form-input" placeholder={form.retainerUsePercent ? 'Enter percentage' : 'Enter monthly retainer'} value={form.retainerAmount} onChange={(e) => setField('retainerAmount', e.target.value.replace(/[^0-9.]/g, ''))} />
                                        {form.retainerUsePercent && <span className="input-suffix" id="dealRetainerSuffix">%</span>}
                                    </div>
                                    {form.retainerUsePercent && (
                                        <div className="field-hint">
                                            Resolved: {(() => {
                                                const raw = Number(form.retainerAmount) || 0;
                                                const base = Number(form.dealAmount) || 0;
                                                const resolved = raw > 0 && base > 0 ? Math.round(base * raw / 100 * 100) / 100 : 0;
                                                return resolved ? formatCurrency(resolved) : (raw > 0 ? 'Enter deal amount' : '—');
                                            })()}
                                        </div>
                                    )}
                                </div>
                                <div className="form-group" data-field="dealEstimatedAnnualValue">
                                    <label htmlFor="dealEstimatedAnnualValue">Estimated Annual Value</label>
                                    <div className="input-adorned"><span className="input-prefix">$</span><Input unstyled type="text" inputMode="decimal" id="dealEstimatedAnnualValue" step="0.01" className="form-input" placeholder="Enter estimated annual value" value={form.estimatedAnnualValue} onChange={(e) => setField('estimatedAnnualValue', e.target.value)} /></div>
                                </div>
                            </div>
                            <div className="deal-form-inline-pair">
                                <div className="form-group">
                                    <label htmlFor="dealOngoingStartDate">Expected Start Date</label>
                                    <Input unstyled type="date" className="form-input" value={form.ongoingStartDate} onChange={(e) => setField('ongoingStartDate', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="dealOngoingEndDate">Expected End Date</label>
                                    <Input unstyled type="date" className="form-input" value={form.ongoingEndDate} onChange={(e) => setField('ongoingEndDate', e.target.value)} />
                                </div>
                            </div>
                            <div className="deal-form-inline-pair">
                                <div className="form-group" data-field="dealMinCommitment">
                                    <label htmlFor="dealMinCommitment">Minimum Commitment</label>
                                    <div className="input-adorned"><span className="input-prefix">$</span><Input unstyled type="text" inputMode="decimal" id="dealMinCommitment" step="0.01" className="form-input" placeholder="Enter minimum commitment" value={form.minCommitment} onChange={(e) => setField('minCommitment', e.target.value)} /></div>
                                    <p className="field-hint">Optional</p>
                                </div>
                                <div className="form-group" data-field="dealMaxBudget">
                                    <label htmlFor="dealMaxBudget">Maximum Budget</label>
                                    <div className="input-adorned"><span className="input-prefix">$</span><Input unstyled type="text" inputMode="decimal" id="dealMaxBudget" step="0.01" className="form-input" placeholder="Enter maximum budget" value={form.maxBudget} onChange={(e) => setField('maxBudget', e.target.value)} /></div>
                                    <p className="field-hint">Optional</p>
                                </div>
                            </div>
                            <div className="form-group full-width">
                                <label htmlFor="dealOngoingInvoiceFrequency">Invoice Frequency</label>
                                <DealFormSelect
                                    id="dealOngoingInvoiceFrequency"
                                    value={form.ongoingInvoiceFrequency}
                                    allowEmpty={false}
                                    onValueChange={(v) => setField('ongoingInvoiceFrequency', v)}
                                    options={[
                                        { value: 'monthly', label: 'Monthly' },
                                        { value: 'quarterly', label: 'Quarterly' },
                                        { value: 'annual', label: 'Annual' },
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {panelForModel === 'milestone' && (
                    <div id="dealMilestonePanel" className="billing-schedule-panel">
                        <div className="billing-section-header">
                            <label className="billing-section-label">Milestones</label>
                        </div>
                        <div className="billing-table-wrapper">
                            <table className="billing-table" id="dealMilestoneTable">
                                <thead><tr><th>#</th><th>Milestone Name</th><th>Due Date</th><th>Amount</th><th></th></tr></thead>
                                <tbody id="dealMilestoneRows">
                                    {(form.dealMilestones || []).map((m, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td><Input unstyled type="text" className="form-input" value={m.title || ''} onChange={(e) => updateMilestone(i, { title: e.target.value })} /></td>
                                            <td><Input unstyled type="date" className="form-input" value={m.dueDate || ''} onChange={(e) => updateMilestone(i, { dueDate: e.target.value })} /></td>
                                            <td><Input unstyled type="text" inputMode="decimal" className="form-input" value={m.amount || ''} onChange={(e) => updateMilestone(i, { amount: e.target.value })} /></td>
                                            <td><button type="button" className="card-action-btn link-danger" onClick={() => removeMilestone(i)}>Remove</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Button type="button" className="btn btn-secondary btn-sm billing-add-btn" variant="secondary" size="sm" id="dealAddMilestoneBtn" onClick={addMilestone}>+ Add Milestone</Button>
                        <div className="billing-schedule-totals">
                            <span>Scheduled: <strong>{formatCurrencyShort(milestoneScheduled)}</strong></span>
                            <span>Remaining: <strong>{formatCurrencyShort(Math.max(0, milestoneRemaining))}</strong></span>
                        </div>
                    </div>
                )}

                {panelForModel === 'custom' && (
                    <div id="dealCustomSchedulePanel" className="billing-schedule-panel">
                        <div className="billing-section-header">
                            <label className="billing-section-label">Custom Invoice Schedule</label>
                        </div>
                        <div className="billing-table-wrapper">
                            <table className="billing-table" id="dealCustomScheduleTable">
                                <thead><tr><th>#</th><th>Invoice Date</th><th>Amount</th><th>Notes</th><th></th></tr></thead>
                                <tbody id="dealCustomScheduleRows">
                                    {(form.customScheduleRows || []).map((r, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td><Input unstyled type="date" className="form-input" value={r.invoiceDate || ''} onChange={(e) => updateCustomRow(i, { invoiceDate: e.target.value })} /></td>
                                            <td><Input unstyled type="text" inputMode="decimal" className="form-input" value={r.amount || ''} onChange={(e) => updateCustomRow(i, { amount: e.target.value })} /></td>
                                            <td><Input unstyled type="text" className="form-input" value={r.notes || ''} onChange={(e) => updateCustomRow(i, { notes: e.target.value })} /></td>
                                            <td><button type="button" className="card-action-btn link-danger" onClick={() => removeCustomRow(i)}>Remove</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Button type="button" className="btn btn-secondary btn-sm billing-add-btn" variant="secondary" size="sm" id="dealAddCustomRowBtn" onClick={addCustomRow}>+ Add Invoice</Button>
                        <div className="billing-schedule-totals">
                            <span>Scheduled: <strong>{formatCurrencyShort(customScheduled)}</strong></span>
                            <span>Remaining: <strong>{formatCurrencyShort(Math.max(0, customRemaining))}</strong></span>
                        </div>
                    </div>
                )}
            </fieldset>

            <DealRevenueSchedulePanel
                dealId={form.id}
                dealSnapshot={{
                    id: form.id,
                    dealStage: form.dealStage,
                    status: form.status,
                    contractValue,
                    dealAmount: Number(form.dealAmount) || 0,
                    paymentModel: form.dealPaymentModel,
                    paymentTerms: form.dealPaymentTerms,
                    paymentCount: form.dealPaymentCount,
                    retainerAmount: form.retainerAmount,
                    estimatedAnnualValue: form.estimatedAnnualValue,
                    oneTimeAmount: form.oneTimeAmount,
                    recurringScheduleRows: form.recurringScheduleRows,
                    customScheduleRows: form.customScheduleRows,
                    dealMilestones: form.dealMilestones,
                }}
            />
        </div>
    );
}
